// Sistema de follows — Wave E backend-backed.
//
// Persistencia canónica: backend `/api/follows/*` (Mongo collection `follows`).
// Caché local: `lineage_follow_graph` = { [followerUsername]: [targetUsername, ...] }
// Eventos: `lineage_follows_updated` (mantenido para compatibilidad con hooks)
//
// Patrón (igual que Waves C/D):
//   * Reads sincrónicos siguen leyendo del caché localStorage — los hooks
//     existentes (BarberProfile, Connections, etc.) no requieren refactor.
//   * Writes (follow/unfollow/toggle) llaman al backend primero, mirroran al
//     caché local y emiten el evento de actualización.
//   * `hydrateFollowsFromBackend(user)` se llama tras login para refrescar el
//     caché con la verdad del backend.

import { awardPoints, ACTIVITY } from '@/utils/clientRewards';
import { followsApi } from '@/lib/followsApi';
import { tr } from '@/contexts/LanguageContext';

const GRAPH_KEY = 'lineage_follow_graph';
const LEGACY_KEY = 'lineage_following';
const SEEDED_KEY_PREFIX = 'lineage_follow_seeded_';
const CLEAN_FLAG_PREFIX = 'lineage_follow_cleaned_v2_';

// ============================================================
// Local cache primitives
// ============================================================
const readGraph = () => {
  try {
    return JSON.parse(localStorage.getItem(GRAPH_KEY) || '{}');
  } catch {
    return {};
  }
};

const writeGraph = (g) => {
  localStorage.setItem(GRAPH_KEY, JSON.stringify(g));
  window.dispatchEvent(new Event('lineage_follows_updated'));
};

// Migración desde versión antigua (`lineage_following` = [username,...]).
const migrateLegacy = (currentUsername) => {
  if (!currentUsername) return;
  const graph = readGraph();
  if (graph[currentUsername] !== undefined) return;
  const legacy = JSON.parse(localStorage.getItem(LEGACY_KEY) || '[]');
  if (legacy.length > 0) {
    graph[currentUsername] = [...legacy];
    writeGraph(graph);
  }
};

// Seed: BETA HONESTY CLEANUP — formerly injected 3 mock followings and 8
// mock followers per user so the empty graph never looked dead. The closed
// beta forbids ANY synthetic relationship, so this function now ONLY:
//   1. Migrates the legacy `lineage_following` array (if present) into the
//      new graph structure for the current user.
//   2. Wipes any previously-seeded mock follow edges from this device.
// It never adds new edges. The user must follow real barbers to see a
// non-empty graph.
const MOCK_BARBER_USERNAMES = new Set([
  'fade_master', 'style_king', 'classic_cuts', 'beard_boss', 'sharp_edges',
  'design_pro', 'urban_cuts', 'precision_fade'
]);

export const seedFollowsForUser = (user) => {
  if (!user || !user.username) return;
  const cleanFlag = CLEAN_FLAG_PREFIX + (user.id || user.username);

  // One-shot migration from legacy `lineage_following`.
  migrateLegacy(user.username);

  if (localStorage.getItem(cleanFlag)) return;

  // Strip any mock-barber edges that earlier seeds may have left behind.
  const graph = readGraph();
  let mutated = false;
  Object.keys(graph).forEach((follower) => {
    if (!Array.isArray(graph[follower])) return;
    const cleaned = graph[follower].filter((u) => !MOCK_BARBER_USERNAMES.has(u));
    if (cleaned.length !== graph[follower].length) {
      graph[follower] = cleaned;
      mutated = true;
    }
    if (MOCK_BARBER_USERNAMES.has(follower)) {
      delete graph[follower];
      mutated = true;
    }
  });
  if (mutated) writeGraph(graph);

  // Drop the legacy "already seeded" marker so the wipe is idempotent.
  localStorage.removeItem(SEEDED_KEY_PREFIX + (user.id || user.username));
  localStorage.setItem(cleanFlag, '1');
};

// ============================================================
// Reads (sync — del caché)
// ============================================================
export const getFollowingUsernames = (username) => {
  if (!username) return [];
  return readGraph()[username] || [];
};

export const getFollowerUsernames = (username) => {
  if (!username) return [];
  const g = readGraph();
  const followers = [];
  Object.entries(g).forEach(([follower, list]) => {
    if (Array.isArray(list) && list.includes(username) && follower !== username) {
      followers.push(follower);
    }
  });
  return followers;
};

export const isFollowing = (followerUsername, targetUsername) => {
  if (!followerUsername || !targetUsername) return false;
  return getFollowingUsernames(followerUsername).includes(targetUsername);
};

// P1-2 (Beta Sprint 1) — siembra una arista confirmada por el backend (p.ej.
// `is_following` de un post recién fetcheado) en la caché sin pasar por un
// toggle. writeGraph emite `lineage_follows_updated` → converge TODAS las
// superficies del mismo autor.
export const seedFollowEdge = (followerUsername, targetUsername) => {
  if (!followerUsername || !targetUsername) return;
  const g = readGraph();
  const list = g[followerUsername] || [];
  if (!list.includes(targetUsername)) {
    g[followerUsername] = [...list, targetUsername];
    writeGraph(g);
  }
};

export const getFollowersCount = (username) => getFollowerUsernames(username).length;
export const getFollowingCount = (username) => getFollowingUsernames(username).length;

// ============================================================
// Writes (async — backend-backed con sync al caché)
// ============================================================
const _cacheAddEdge = (follower, target) => {
  if (!follower || !target) return;
  const g = readGraph();
  const list = g[follower] || [];
  if (!list.includes(target)) {
    g[follower] = [...list, target];
    writeGraph(g);
  }
};

const _cacheRemoveEdge = (follower, target) => {
  if (!follower || !target) return;
  const g = readGraph();
  const list = g[follower] || [];
  g[follower] = list.filter((u) => u !== target);
  writeGraph(g);
};

export const follow = async (followerUsername, targetUsername) => {
  if (!followerUsername || !targetUsername || followerUsername === targetUsername) return false;
  try {
    await followsApi.follow(targetUsername);
    _cacheAddEdge(followerUsername, targetUsername);
    return true;
  } catch (err) {
    if (err?.status === 400) return false; // self-follow blocked
    throw err;
  }
};

export const unfollow = async (followerUsername, targetUsername) => {
  if (!followerUsername || !targetUsername) return;
  await followsApi.unfollow(targetUsername);
  _cacheRemoveEdge(followerUsername, targetUsername);
};

/**
 * Toggle backend-backed. Returns `true` if now following.
 * Awards client-fan points only for new follows (barbers/admin don't earn).
 * Throws a friendly DemoBarberError when the target is a mock-only user
 * that doesn't exist in the backend (404).
 */
export const toggleFollow = async (followerUsername, targetUsername, actor) => {
  if (!followerUsername || !targetUsername || followerUsername === targetUsername) {
    return false;
  }
  // Optimista (rendimiento D): la caché local cambia al instante (todas las pantallas que escuchan
  // `lineage_follows_updated` se actualizan); si el backend falla, se revierte.
  const wasFollowing = isFollowing(followerUsername, targetUsername);
  if (wasFollowing) _cacheRemoveEdge(followerUsername, targetUsername); else _cacheAddEdge(followerUsername, targetUsername);
  let res;
  try {
    res = await followsApi.toggle(targetUsername);
  } catch (err) {
    if (wasFollowing) _cacheAddEdge(followerUsername, targetUsername); else _cacheRemoveEdge(followerUsername, targetUsername);
    if (err?.status === 404) {
      const friendly = new Error(tr("Este barbero es solo demo aún — pronto podrás seguirlo"));
      friendly.status = 404;
      friendly.isMockBarber = true;
      throw friendly;
    }
    throw err;
  }
  // HOTFIX Jul-2026: el backend ahora responde el estado VERIFICADO en DB
  // (following, follower_count, following_count, relationship_id). La caché
  // local solo refleja esa verdad — nunca al revés.
  if (res.following) {
    _cacheAddEdge(followerUsername, targetUsername);
    if (actor?.id && (actor.role === 'client' || actor.role === 'spectator')) {
      try { awardPoints(actor.id, ACTIVITY.FOLLOW_BARBER.id, { target: targetUsername }); }
      catch { /* best-effort */ }
    }
  } else {
    _cacheRemoveEdge(followerUsername, targetUsername);
  }
  // Evento autoritativo con contadores frescos para Perfil/Connections.
  window.dispatchEvent(new CustomEvent('lineage_follow_updated', {
    detail: {
      follower: followerUsername,
      target: targetUsername,
      following: res.following,
      follower_count: res.follower_count,
      following_count: res.following_count,
    },
  }));
  return res.following;
};

// ============================================================
// Hydration
// ============================================================
/**
 * Pulls the current user's following list + their followers list from backend
 * and refreshes the local cache. Called on app bootstrap / after login.
 *
 * Strategy:
 *   - Set graph[me] = [my followings...] (replaces whatever was cached)
 *   - For each follower of me, ensure graph[follower] includes me (additive,
 *     does NOT clobber that follower's other followings — we don't know them)
 */
export const hydrateFollowsFromBackend = async (user) => {
  if (!user?.username) return;
  try {
    const [myFollowing, myFollowers] = await Promise.all([
      followsApi.following(user.username, { limit: 500 }).catch(() => ({ items: [] })),
      followsApi.followers(user.username, { limit: 500 }).catch(() => ({ items: [] })),
    ]);
    const g = readGraph();
    g[user.username] = (myFollowing.items || []).map((u) => u.username);
    (myFollowers.items || []).forEach((follower) => {
      const list = g[follower.username] || [];
      if (!list.includes(user.username)) {
        g[follower.username] = [...list, user.username];
      }
    });
    writeGraph(g);
  } catch {
    /* offline OK */
  }
};

// ============================================================
// Resolver a objetos user (backend registry only — no mock fallback).
// ============================================================
export const resolveUsersByUsernames = (usernames) => {
  if (!Array.isArray(usernames) || usernames.length === 0) return [];
  const registry = JSON.parse(localStorage.getItem('lineage_users') || '{}');
  return usernames.map((u) => {
    const reg = registry[u];
    if (reg) return { ...reg, isMock: false };
    return {
      id: `u_unknown_${u}`,
      username: u,
      name: u,
      avatar: '',
      role: 'client',
      isMock: false
    };
  });
};
